import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import ImageDropZone from "./ImageDropZone";
import { compressToWebP, withUploadTimeout } from "@/lib/imageCompression";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";

const SLOTS = [
  {
    slot: "logo",
    label: "Logo du site",
    hint: "PNG transparent recommandé. Affiché en blanc dans la barre de navigation.",
    accept: "image/png,image/svg+xml,image/webp",
  },
  {
    slot: "favicon",
    label: "Favicon (onglet & Google Search)",
    hint: "PNG carré 512×512. Mis à jour instantanément sur le site et indexé par Google.",
    accept: "image/png,image/x-icon,image/svg+xml",
  },
];

const AdminBranding = () => {
  const [images, setImages] = useState<Record<string, string>>({});
  const [logoHeight, setLogoHeight] = useState<number>(40);
  const [savingLogoSize, setSavingLogoSize] = useState(false);

  useEffect(() => {
    supabase
      .from("site_images")
      .select("*")
      .in("slot", ["logo", "favicon"])
      .then(({ data }) => {
        const map: Record<string, string> = {};
        data?.forEach((img) => {
          map[img.slot] = img.url;
        });
        setImages(map);
      });
    supabase
      .from("site_content")
      .select("value")
      .eq("key", "ui.logo_height_px")
      .maybeSingle()
      .then(({ data }) => {
        const n = Number(data?.value);
        if (Number.isFinite(n) && n > 0) setLogoHeight(n);
      });
  }, []);

  const saveLogoHeight = async () => {
    setSavingLogoSize(true);
    const { error } = await supabase
      .from("site_content")
      .upsert({ key: "ui.logo_height_px", value: String(logoHeight) }, { onConflict: "key,lang" });
    setSavingLogoSize(false);
    if (error) toast.error(error.message);
    else toast.success(`Taille du logo enregistrée (${logoHeight}px)`);
  };

  const uploadImage = async (slot: string, file: File) => {
    // SVG / ICO are vector / icon formats — keep as-is. Raster files get compressed to WebP.
    const isVector = /svg|icon|ico/i.test(file.type) || /\.(svg|ico)$/i.test(file.name);
    const uploadFile = isVector ? file : await compressToWebP(file, { maxSizeMB: 0.5 });
    const ext = isVector
      ? (file.name.split(".").pop() || "png").toLowerCase()
      : "webp";
    const path = `${slot}.${ext}`;

    await supabase.storage
      .from("site-images")
      .remove([`${slot}.png`, `${slot}.jpg`, `${slot}.jpeg`, `${slot}.webp`, `${slot}.svg`, `${slot}.ico`])
      .catch(() => {});

    const { error: uploadError } = await withUploadTimeout(
      supabase.storage
        .from("site-images")
        .upload(path, uploadFile, { upsert: true, contentType: uploadFile.type })
    );
    if (uploadError) throw new Error("Erreur upload : " + uploadError.message);

    const { data: urlData } = supabase.storage.from("site-images").getPublicUrl(path);
    const url = urlData.publicUrl + "?t=" + Date.now();

    const { error: dbError } = await supabase
      .from("site_images")
      .upsert({ slot, url }, { onConflict: "slot" });
    if (dbError) throw new Error("Erreur DB : " + dbError.message);

    setImages((prev) => ({ ...prev, [slot]: url }));
    toast.success(`${slot === "favicon" ? "Favicon" : "Logo"} mis à jour !`);

    // Live-update the favicon links so the change is instantly visible
    if (slot === "favicon") {
      ["icon", "shortcut icon", "apple-touch-icon"].forEach((rel) => {
        let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
        if (!link) {
          link = document.createElement("link");
          link.rel = rel;
          document.head.appendChild(link);
        }
        link.href = url;
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card/50 p-4">
        <h3 className="font-heading text-lg font-semibold text-foreground">Identité de marque</h3>
        <p className="font-body text-sm text-muted-foreground mt-1">
          Téléversez votre logo et votre favicon. Les fichiers remplacent automatiquement les versions
          précédentes dans le stockage et sont propagés instantanément sur le site.
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        {SLOTS.map(({ slot, label, hint, accept }) => (
          <Card key={slot} className="border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">{label}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ImageDropZone
                currentUrl={images[slot]}
                onUpload={(file) => uploadImage(slot, file)}
                accept={accept}
                label="Glissez-déposez ou cliquez pour uploader"
                height="h-48"
              />
              <p className="font-body text-xs text-muted-foreground">{hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Taille du logo sur le site</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Slider
                value={[logoHeight]}
                min={24}
                max={96}
                step={2}
                onValueChange={(v) => setLogoHeight(v[0] ?? 40)}
              />
            </div>
            <div className="w-20 text-right font-mono text-sm">{logoHeight} px</div>
            <Button onClick={saveLogoHeight} disabled={savingLogoSize} className="bg-primary text-primary-foreground">
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </div>
          {images.logo && (
            <div className="flex items-center gap-3 rounded-md border border-border bg-secondary/40 px-3 py-2">
              <span className="text-xs text-muted-foreground">Aperçu :</span>
              <img src={images.logo} alt="Logo" style={{ height: `${logoHeight}px` }} className="w-auto object-contain" />
            </div>
          )}
          <p className="font-body text-xs text-muted-foreground">
            Ajuste la hauteur du logo dans la barre de navigation et le pied de page. La largeur s'adapte automatiquement.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBranding;